import {
  LayoutGrid,
  File,
  Palette,
  LayoutPanelLeft,
  Library,
  ExternalLink,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  skillsMarketplaceApi,
  slugifySkillName,
} from "@/lib/api/skills-marketplace";

type MentionBadgeType = "project" | "file" | "theme" | "skill" | "asset";

const CUSTOM_SKILL_PREFIX = "custom-";

// id is the routing slug: marketplace skills use their catalog id directly;
// custom skills use "custom-{name-slug}", which must be resolved back to the
// custom skill's DB id before we can build its detail URL.
function openSkillPage(id?: string): void {
  if (!id) {
    window.open("/dashboard/skills", "_blank");
    return;
  }
  if (!id.startsWith(CUSTOM_SKILL_PREFIX)) {
    window.open(`/dashboard/skills/${id}`, "_blank");
    return;
  }
  // Open the tab synchronously so popup blockers allow it, then redirect it
  // once the slug → DB id lookup resolves.
  const win = window.open("", "_blank");
  const slug = id.slice(CUSTOM_SKILL_PREFIX.length);
  skillsMarketplaceApi
    .getCustomSkills()
    .then((skills) => {
      const match = skills.find((s) => slugifySkillName(s.name) === slug);
      const url = match
        ? `/dashboard/skills/custom/${match.id}`
        : "/dashboard/skills";
      if (win) win.location.href = url;
      else window.open(url, "_blank");
    })
    .catch(() => {
      if (win) win.location.href = "/dashboard/skills";
    });
}

interface MentionBadgeProps {
  type: MentionBadgeType;
  name: string;
  id?: string;
  onRemove?: () => void;
  /** Use "onPrimary" when the badge is rendered on a primary-colored background (user chat bubble) */
  variant?: "default" | "onPrimary";
}

const STYLES: Record<MentionBadgeType, string> = {
  file: "bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:bg-blue-400/10 dark:border-blue-400/25 dark:text-blue-300",
  project:
    "bg-violet-500/10 border border-violet-500/30 text-violet-600 dark:bg-violet-400/10 dark:border-violet-400/25 dark:text-violet-300",
  theme:
    "bg-pink-500/10 border border-pink-500/30 text-pink-600 dark:bg-pink-400/10 dark:border-pink-400/25 dark:text-pink-300",
  skill:
    "bg-purple-500/10 border border-purple-500/30 text-purple-600 dark:bg-purple-400/10 dark:border-purple-400/25 dark:text-purple-300",
  asset:
    "bg-indigo-500/10 border border-indigo-500/30 text-indigo-600 dark:bg-indigo-400/10 dark:border-indigo-400/25 dark:text-indigo-300",
};

// When rendered inside the user chat bubble — mirror the bubble's zinc palette so the
// chip text stays readable in both themes (the bubble is light-gray in light mode,
// dark in dark mode, so a hardcoded white was invisible against the light-mode bubble).
const ON_PRIMARY_STYLE =
  "bg-zinc-900/5 border border-zinc-900/15 text-zinc-700 dark:bg-white/15 dark:border-white/30 dark:text-white";

const ICONS: Record<MentionBadgeType, typeof LayoutGrid> = {
  project: LayoutGrid,
  file: File,
  theme: Palette,
  skill: LayoutPanelLeft,
  asset: Library,
};

export function MentionBadge({
  type,
  name,
  id,
  onRemove,
  variant = "default",
}: MentionBadgeProps) {
  const Icon = ICONS[type];

  const handleOpen = () => {
    if (type === "project" && id) {
      window.open(`/dashboard/editor/${id}`, "_blank");
    } else if (type === "skill") {
      openSkillPage(id);
    }
  };

  const isClickable = (type === "project" && !!id) || type === "skill";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium shadow-sm",
        variant === "onPrimary" ? ON_PRIMARY_STYLE : STYLES[type],
      )}
    >
      {type === "asset" ? (
        <img
          src="/optisigns-logo.svg"
          alt=""
          className="w-3.5 h-3.5 rounded-[2px] flex-shrink-0"
        />
      ) : (
        <Icon className="w-3.5 h-3.5 opacity-80 flex-shrink-0" />
      )}
      {isClickable ? (
        <button
          type="button"
          onClick={handleOpen}
          className="hover:underline"
          title={type === "skill" ? `Skill: ${name}` : `Project: ${name}`}
        >
          {name}
        </button>
      ) : (
        <span>{name}</span>
      )}
      {isClickable && !onRemove && (
        <ExternalLink className="w-2.5 h-2.5 opacity-60" />
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 opacity-70 hover:opacity-100"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}
